import { useState } from "react";
import { api, buildWhatsAppLink } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Loader2, Mail, Phone, MessageCircle, MapPin } from "lucide-react";

export default function Contact() {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    customer_name: "",
    email: "",
    phone: "",
    message: "",
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.customer_name || !form.email || !form.phone) {
      toast.error("Please share your name, email and phone.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/inquiries", {
        ...form,
        travelers: 1,
        package_title: "General Inquiry",
      });
      toast.success("Thanks! We'll get back to you within 24 hours.");
      setForm({ customer_name: "", email: "", phone: "", message: "" });
    } catch {
      toast.error("Could not send. Please try WhatsApp instead.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="bg-[color:var(--brand-surface-alt)] border-b border-[color:var(--brand-border)]">
        <div className="container-narrow py-20">
          <div className="overline">Say hello</div>
          <h1 className="font-heading text-5xl sm:text-6xl mt-3 max-w-3xl leading-tight">
            Reach out. We answer, always.
          </h1>
          <p className="mt-4 max-w-xl text-[color:var(--brand-text-soft)]">
            Prefer WhatsApp, email or a call — pick your flavour. Our team
            usually replies within a few hours.
          </p>
        </div>
      </section>

      <section className="container-narrow py-16 grid md:grid-cols-2 gap-16">
        <div>
          <h2 className="font-heading text-3xl">Reach us directly</h2>
          <ul className="mt-8 space-y-6">
            <li className="flex gap-4" data-testid="contact-phone">
              <Phone className="w-5 h-5 text-[color:var(--brand-primary)] mt-1" />
              <div>
                <div className="overline">Phone</div>
                <a href="tel:+918900964069" className="text-lg font-heading block">
                  +91 89009 64069
                </a>
                <a href="tel:+919679528328" className="text-lg font-heading block" data-testid="contact-phone-alt">
                  +91 96795 28328
                </a>
              </div>
            </li>
            <li className="flex gap-4" data-testid="contact-email">
              <Mail className="w-5 h-5 text-[color:var(--brand-primary)] mt-1" />
              <div>
                <div className="overline">Email</div>
                <a href="mailto:journeytoandaman@gmail.com" className="text-lg font-heading">
                  journeytoandaman@gmail.com
                </a>
              </div>
            </li>
            <li className="flex gap-4">
              <MessageCircle className="w-5 h-5 text-[color:var(--brand-whatsapp)] mt-1" />
              <div>
                <div className="overline">WhatsApp</div>
                <a
                  href={buildWhatsAppLink()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="link-underline"
                  data-testid="contact-whatsapp"
                >
                  Chat with us now
                </a>
              </div>
            </li>
            <li className="flex gap-4">
              <MapPin className="w-5 h-5 text-[color:var(--brand-primary)] mt-1" />
              <div>
                <div className="overline">Office</div>
                <p className="text-lg font-heading">Port Blair, A&amp;N Islands</p>
              </div>
            </li>
          </ul>
        </div>

        <form onSubmit={submit} className="border border-[color:var(--brand-border)] p-8 bg-[color:var(--brand-surface)] space-y-5" data-testid="contact-form">
          <div>
            <Label htmlFor="c_name">Full name</Label>
            <Input
              id="c_name"
              value={form.customer_name}
              onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
              data-testid="contact-input-name"
              required
            />
          </div>
          <div>
            <Label htmlFor="c_email">Email</Label>
            <Input
              id="c_email"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              data-testid="contact-input-email"
              required
            />
          </div>
          <div>
            <Label htmlFor="c_phone">Phone</Label>
            <Input
              id="c_phone"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              data-testid="contact-input-phone"
              required
            />
          </div>
          <div>
            <Label htmlFor="c_msg">Message</Label>
            <Textarea
              id="c_msg"
              rows={4}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              placeholder="Tell us what you'd love to experience…"
              data-testid="contact-input-message"
            />
          </div>
          <Button type="submit" disabled={loading} className="btn-primary w-full" data-testid="contact-submit">
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            Send message
          </Button>
        </form>
      </section>
    </div>
  );
}
